import BASE_URL from '../../utils/storage.js';
import { parseRetryAfter } from '../../api/errors.js';

// Custom error class for authentication
class AuthError extends Error {
  constructor(message, statusCode, errorCode) {
    super(message);
    this.statusCode = statusCode;
    this.errorCode = errorCode;
    this.name = 'AuthError';
  }
}

const withRetryAfter = (error, response) => {
  error.retryAfter = parseRetryAfter(response.headers.get('Retry-After')) || 60;
  return error;
};

export const auth2FAAPI = {
  // ============ LOGIN WITH 2FA SUPPORT ============
  async login(phone_number, password, role = null) {
    try {
      const payload = { phone_number, password };
      if (role) {
        payload.role = role;
      }

      const response = await fetch(`${BASE_URL}auth/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error codes
        if (response.status === 409 && data.error === 'multiple_accounts') {
          const error = new AuthError(
            data.message || 'Există mai multe conturi active pentru acest număr.',
            409,
            'MULTIPLE_ACCOUNTS'
          );
          error.accounts = Array.isArray(data.accounts) ? data.accounts : [];
          throw error;
        }

        if (response.status === 429) {
          throw withRetryAfter(new AuthError(
            'Prea multe încercări. Așteaptă un minut și încearcă din nou.',
            429,
            'RATE_LIMIT'
          ), response);
        } else if (response.status === 401) {
          throw new AuthError(
            data.error || 'Număr de telefon sau parolă incorectă.',
            401,
            'INVALID_CREDENTIALS'
          );
        } else if (response.status === 400) {
          if (data.error?.includes('2FA is enabled but no email')) {
            throw new AuthError(
              'Contul tău are 2FA activat dar nu are email setat. Contactează administratorul.',
              400,
              'NO_EMAIL_FOR_2FA'
            );
          }
          throw new AuthError(
            data.error || 'Date invalide.',
            400,
            'BAD_REQUEST'
          );
        }
        throw new AuthError(
          data.error || 'Eroare la autentificare.',
          response.status,
          'AUTH_ERROR'
        );
      }

      // Check if 2FA is required
      if (data.requires_2fa) {
        return {
          requires2FA: true,
          tempToken: data.temp_token,
          message: data.message || 'Un cod de verificare a fost trimis pe email.'
        };
      }

      // Regular login without 2FA
      return {
        requires2FA: false,
        user: data.user,
        token: data.token,
        expiry: data.expiry
      };
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'Nu se poate conecta la server. Verifică conexiunea la internet.',
        0,
        'NETWORK_ERROR'
      );
    }
  },

  // ============ VERIFY 2FA CODE ============
  async verify2FA(tempToken, code) {
    try {
      const response = await fetch(`${BASE_URL}auth/2fa/verify/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          temp_token: tempToken,
          code: code
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Parse attempts remaining from error message
        const attemptsMatch = data.error?.match(/(\d+) attempts remaining/);
        const attemptsRemaining = attemptsMatch ? parseInt(attemptsMatch[1]) : null;

        if (response.status === 429) {
          throw withRetryAfter(new AuthError(
            'Prea multe încercări. Așteaptă un minut și încearcă din nou.',
            429,
            'RATE_LIMIT'
          ), response);
        } else if (data.error?.includes('Too many failed attempts')) {
          throw new AuthError(
            'Ai greșit codul de 5 ori. Solicită un cod nou.',
            400,
            'TOO_MANY_ATTEMPTS'
          );
        } else if (data.error?.includes('expired')) {
          throw new AuthError(
            'Codul a expirat. Solicită un cod nou.',
            400,
            'CODE_EXPIRED'
          );
        } else if (data.error?.includes('Invalid code')) {
          const error = new AuthError(
            `Cod incorect. ${attemptsRemaining ? `${attemptsRemaining} încercări rămase.` : ''}`,
            400,
            'INVALID_CODE'
          );
          error.attemptsRemaining = attemptsRemaining;
          throw error;
        } else if (data.error?.includes('Invalid or expired verification session')) {
          throw new AuthError(
            'Sesiunea de verificare a expirat. Te rugăm să te autentifici din nou.',
            400,
            'SESSION_EXPIRED'
          );
        }

        throw new AuthError(
          data.error || 'Eroare la verificarea codului.',
          response.status,
          'VERIFY_ERROR'
        );
      }

      return {
        user: data.user,
        token: data.token,
        expiry: data.expiry
      };
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'Nu se poate conecta la server. Verifică conexiunea la internet.',
        0,
        'NETWORK_ERROR'
      );
    }
  },

  // ============ RESEND 2FA CODE ============
  async resend2FACode(tempToken) {
    try {
      const response = await fetch(`${BASE_URL}auth/2fa/resend/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ temp_token: tempToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          if (data.error?.includes('Please wait 15 minutes')) {
            throw withRetryAfter(new AuthError(
              'Ai solicitat prea multe coduri. Așteaptă 15 minute.',
              429,
              'TOO_MANY_CODES'
            ), response);
          }
          throw withRetryAfter(new AuthError(
            'Prea multe cereri. Așteaptă un minut și încearcă din nou.',
            429,
            'RATE_LIMIT'
          ), response);
        } else if (data.error?.includes('Invalid or expired')) {
          throw new AuthError(
            'Sesiunea a expirat. Te rugăm să te autentifici din nou.',
            400,
            'SESSION_EXPIRED'
          );
        }

        throw new AuthError(
          data.error || 'Eroare la retrimiterea codului.',
          response.status,
          'RESEND_ERROR'
        );
      }

      // IMPORTANT: Return the NEW temp_token
      return {
        tempToken: data.temp_token, // New token!
        message: data.message || 'Un nou cod a fost trimis pe email.'
      };
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'Nu se poate conecta la server. Verifică conexiunea la internet.',
        0,
        'NETWORK_ERROR'
      );
    }
  },

  // ============ LOGOUT CURRENT DEVICE ============
  async logout(authToken) {
    try {
      const response = await fetch(`${BASE_URL}auth/logout/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${authToken}`
        },
      });

      if (!response.ok) {
        // Don't throw on logout errors, just log them
        console.error('Logout error:', response.status);
      }

      return true;
    } catch (error) {
      // Don't throw here as we want to clear local state regardless
      console.error('Logout network error:', error);
      return true;
    }
  },

  // ============ LOGOUT ALL DEVICES ============
  async logoutAllDevices(authToken) {
    try {
      const response = await fetch(`${BASE_URL}auth/logout-all/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${authToken}`
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new AuthError(
          data.error || 'Eroare la deconectarea tuturor dispozitivelor.',
          response.status,
          'LOGOUT_ALL_ERROR'
        );
      }

      return true;
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'Nu se poate conecta la server. Verifică conexiunea la internet.',
        0,
        'NETWORK_ERROR'
      );
    }
  },

  // ============ GET ACTIVE SESSIONS ============
  async getActiveSessions(authToken) {
    try {
      const response = await fetch(`${BASE_URL}auth/sessions/`, {
        headers: {
          "Authorization": `Token ${authToken}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new AuthError(
          data.error || 'Eroare la obținerea sesiunilor.',
          response.status,
          'SESSIONS_ERROR'
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'Nu se poate conecta la server. Verifică conexiunea la internet.',
        0,
        'NETWORK_ERROR'
      );
    }
  },

  // ============ REVOKE SESSION ============
  async revokeSession(authToken, sessionId) {
    try {
      const response = await fetch(`${BASE_URL}auth/sessions/${sessionId}/revoke/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${authToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          throw new AuthError(
            'Sesiunea nu a fost găsită.',
            404,
            'SESSION_NOT_FOUND'
          );
        } else if (data.error?.includes('Cannot revoke current session')) {
          throw new AuthError(
            'Nu poți revoca sesiunea curentă. Folosește Logout.',
            400,
            'CANNOT_REVOKE_CURRENT'
          );
        }

        throw new AuthError(
          data.error || 'Eroare la revocarea sesiunii.',
          response.status,
          'REVOKE_ERROR'
        );
      }

      return data;
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'Nu se poate conecta la server. Verifică conexiunea la internet.',
        0,
        'NETWORK_ERROR'
      );
    }
  },

  // ============ GET 2FA STATUS ============
  async get2FAStatus(authToken) {
    try {
      const response = await fetch(`${BASE_URL}auth/2fa/status/`, {
        headers: {
          "Authorization": `Token ${authToken}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new AuthError(
          data.error || 'Eroare la obținerea statusului 2FA.',
          response.status,
          '2FA_STATUS_ERROR'
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'Nu se poate conecta la server. Verifică conexiunea la internet.',
        0,
        'NETWORK_ERROR'
      );
    }
  },

  // ============ ENABLE 2FA ============
  async enable2FA(authToken) {
    try {
      const response = await fetch(`${BASE_URL}auth/2fa/enable/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${authToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error?.includes('no email')) {
          throw new AuthError(
            'Nu poți activa 2FA fără o adresă de email validă.',
            400,
            'NO_EMAIL'
          );
        }

        throw new AuthError(
          data.error || 'Eroare la activarea 2FA.',
          response.status,
          'ENABLE_2FA_ERROR'
        );
      }

      return data;
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'Nu se poate conecta la server. Verifică conexiunea la internet.',
        0,
        'NETWORK_ERROR'
      );
    }
  },

  // ============ DISABLE 2FA ============
  async disable2FA(authToken, password) {
    try {
      const response = await fetch(`${BASE_URL}auth/2fa/disable/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${authToken}`
        },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new AuthError(
            'Parolă incorectă.',
            401,
            'INVALID_PASSWORD'
          );
        }

        throw new AuthError(
          data.error || 'Eroare la dezactivarea 2FA.',
          response.status,
          'DISABLE_2FA_ERROR'
        );
      }

      return data;
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'Nu se poate conecta la server. Verifică conexiunea la internet.',
        0,
        'NETWORK_ERROR'
      );
    }
  },

  // Keep backward compatibility methods
  async register(userData) {
    const response = await fetch(`${BASE_URL}auth/users/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Registration failed');
    }

    return await response.json();
  },

  async requestPasswordReset(phone_number) {
    const response = await fetch(`${BASE_URL}auth/users/reset_password/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone_number }),
    });

    if (!response.ok) {
      throw new Error('Password reset request failed');
    }

    return await response.json();
  }
};

export { AuthError };
